import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Folder, Edit2, Check, X as XIcon } from "lucide-react";
import { Input } from "@shadcn-in-nx/ui/input";
import { Checkbox } from "@shadcn-in-nx/ui/checkbox";
import Button from "../../../components/Button.jsx";
import WorkflowEmptyState from "./WorkflowEmptyState";

export default function WorkflowGroupedView({
    groups,
    selectedWorkflows,
    onSelectGroup,
    onAppClick,
    onUpdateAppName,
}) {
    const { t } = useTranslation();
    const [editingId, setEditingId] = useState(null);
    const [editingName, setEditingName] = useState("");

    if (groups.length === 0) {
        return <WorkflowEmptyState iconName="Folder" showArchived={false} />;
    }

    const startEdit = (group) => {
        setEditingId(group.id);
        setEditingName(group.title);
    };

    const saveEdit = () => {
        if (editingId && editingName.trim()) {
            onUpdateAppName(editingId, editingName.trim());
        }
        setEditingId(null);
        setEditingName("");
    };

    const handleKey = (e) => {
        if (e.key === "Enter") saveEdit();
        if (e.key === "Escape") {
            setEditingId(null);
            setEditingName("");
        }
    };

    return (
        <div className="space-y-4">
            {groups.map((group) => (
                <div key={group.id} className="border rounded-lg">
                    <div
                        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 group"
                        onClick={() => onAppClick(group.id)}
                    >
                        <div className="flex items-center gap-3">
                            <Folder className="h-5 w-5 text-gray-600" />
                            {editingId === group.id ? (
                                <div
                                    className="flex items-center gap-2"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <Input
                                        value={editingName}
                                        onChange={(e) =>
                                            setEditingName(e.target.value)
                                        }
                                        onKeyDown={handleKey}
                                        className="h-8 text-sm font-medium"
                                        autoFocus
                                    />
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-8 w-8 p-0"
                                        onClick={saveEdit}
                                    >
                                        <Check className="h-4 w-4 text-green-600" />
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-8 w-8 p-0"
                                        onClick={() => setEditingId(null)}
                                    >
                                        <XIcon className="h-4 w-4 text-red-600" />
                                    </Button>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <h3 className="font-medium text-gray-900">
                                        {group.title}
                                    </h3>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            startEdit(group);
                                        }}
                                    >
                                        <Edit2 className="h-3 w-3 text-gray-500" />
                                    </Button>
                                </div>
                            )}
                            <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                {group._count.workflows}{" "}
                                {t("workflowIndex.groupedView.workflows")}
                            </span>
                        </div>
                        <Checkbox
                            checked={group.workflows.every((w) =>
                                selectedWorkflows.has(w.id)
                            )}
                            onCheckedChange={(checked) =>
                                onSelectGroup(group.workflows, checked)
                            }
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                </div>
            ))}
        </div>
    );
}
